import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

type NavigationDirection = 'forward' | 'back';

// ルートの順序定義（左から右へ進む）
const ROUTE_ORDER = ['/', '/reservations', '/announcements', '/customers'];

/**
 * View Transitions APIを使用した画面遷移フック
 *
 * フェイルセーフ機能:
 * - API未対応ブラウザ: 通常遷移（アニメーションなし）
 * - エラー発生時: try-catchで捕捉し通常遷移へフォールバック
 * - タイムアウト: 300ms超過時は強制完了
 * - prefers-reduced-motion: ユーザー設定でアニメーション無効
 */
export function useViewTransition() {
  const location = useLocation();
  const [direction, setDirection] = useState<NavigationDirection>('forward');
  const previousPathRef = useRef<string>(location.pathname);

  // View Transitions APIのサポート検出
  const isSupported =
    typeof document !== 'undefined' &&
    'startViewTransition' in document &&
    // prefers-reduced-motionの確認
    !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  useEffect(() => {
    const currentPath = location.pathname;
    const previousPath = previousPathRef.current;

    // ルート順序から方向を判定
    const getCurrentIndex = (path: string) => {
      // 顧客詳細ページ（/customers/xxx）は index 4（一覧より後）
      if (path.startsWith('/customers/') && path !== '/customers' && path !== '/customers/') {
        console.log('[Direction] Customer detail detected:', path);
        return 4;
      }
      const index = ROUTE_ORDER.findIndex((route) => path === route);
      console.log('[Direction] Route index for', path, ':', index);
      return index;
    };

    const currentIndex = getCurrentIndex(currentPath);
    const previousIndex = getCurrentIndex(previousPath);

    console.log('[Direction] Index comparison:', {
      from: previousPath,
      to: currentPath,
      fromIndex: previousIndex,
      toIndex: currentIndex,
    });

    // 方向判定（デフォルトはforward）
    if (currentIndex !== -1 && previousIndex !== -1) {
      const newDirection = currentIndex > previousIndex ? 'forward' : 'back';
      console.log('[Direction] Result:', newDirection, `(${currentIndex} > ${previousIndex})`);
      setDirection(newDirection);
    } else {
      console.log('[Direction] Result: forward (default)');
      setDirection('forward');
    }

    previousPathRef.current = currentPath;
  }, [location.pathname]);

  /**
   * View Transitionを開始する関数
   * エラー時は通常の動作にフォールバック
   */
  const startTransition = (callback: () => void) => {
    // API未対応 or prefers-reduced-motion の場合は通常実行
    if (!isSupported) {
      callback();
      return;
    }

    try {
      // タイムアウト設定（300ms）
      const timeoutId = setTimeout(() => {
        console.warn('[ViewTransition] Timeout: transition took longer than 300ms');
      }, 300);

      // View Transition API実行
      const transition = (document as any).startViewTransition(() => {
        callback();
      });

      // 完了時にタイムアウトをクリア
      transition.finished
        .then(() => {
          clearTimeout(timeoutId);
        })
        .catch((error: Error) => {
          clearTimeout(timeoutId);
          console.error('[ViewTransition] Error during transition:', error);
        });
    } catch (error) {
      // エラー発生時は通常実行にフォールバック
      console.error('[ViewTransition] Failed to start transition:', error);
      callback();
    }
  };

  return {
    direction,
    isSupported,
    startTransition,
  };
}
